import reduce from 'lodash/reduce';
import BaseModel from './BaseModel';
import {
  NOTIFICATION_DELIVERY_TYPE_SMS,
  NOTIFICATION_DELIVERY_TYPE_EMAIL,
  NOTIFICATION_DELIVERY_TYPE_PUSH,
} from '../constants';
import {
  NOTIFICATION_EVENT_TYPE__CREATE,
  NOTIFICATION_EVENT_TYPE__CANCEL,
  NOTIFICATION_EVENT_TYPE__ATTEMPT,
  NOTIFICATION_EVENT_TYPE__ACCEPT,
  NOTIFICATION_EVENT_TYPE__SEND,
  NOTIFICATION_EVENT_TYPE__ENQUEUE,
  NOTIFICATION_EVENT_TYPE__DELIVER,
  NOTIFICATION_EVENT_TYPE__DELIVERY_FAILURE,
  NOTIFICATION_EVENT_TYPE__RECEIVE,
  NOTIFICATION_EVENT_TYPE__OPEN,
  NOTIFICATION_EVENT_TYPE__CLICK,
  NOTIFICATION_LOG_STATE__INITIAL,
  NOTIFICATION_LOG_STATE__ABORTED,
  NOTIFICATION_LOG_STATE__SENDING,
  NOTIFICATION_LOG_STATE__ACCEPTED,
  NOTIFICATION_LOG_STATE__ENQUEUED,
  NOTIFICATION_LOG_STATE__SENT,
  NOTIFICATION_LOG_STATE__FAILED,
  NOTIFICATION_LOG_STATE__DELIVERED,
  NOTIFICATION_LOG_STATE__RECEIVED,
  NOTIFICATION_LOG_STATE__OPENED,
  NOTIFICATION_LOG_STATE__CLICKED,
  NOTIFICATION_LOG_STATE_PROGRESS,
} from '../constants/collections/NotificationLogs';

// `wasAttempted` says whether the log holds an ATTEMPT anywhere at all, not
// just before this event. Only the CANCEL case uses it, and it has to be
// decided from the whole array: deciding it from what happens to precede the
// cancel in storage would make the same two events resolve two different ways
// depending on the order they were written, which is the fault this model
// exists to remove.
const applyEvent = (current = {}, event, wasAttempted) => {
  switch (event.type) {
    case NOTIFICATION_EVENT_TYPE__CREATE:
      return {
        ...current,
        state: NOTIFICATION_LOG_STATE__INITIAL,
        meta: event.meta,
      };
    case NOTIFICATION_EVENT_TYPE__CANCEL: {
      // A cancel that belongs to a send that was actually tried is a failure
      // of that send. sendMessage raises one from the catch around the
      // provider call, so its logs always hold an ATTEMPT too. A cancel with
      // no attempt anywhere comes from NotificationIntents.sendNotifications,
      // where the log was written but the send never started -- nothing left
      // this server, which is what ABORTED means.
      if (wasAttempted || current.state === NOTIFICATION_LOG_STATE__SENDING) {
        return {
          ...current,
          state: NOTIFICATION_LOG_STATE__FAILED,
          error: event.payload,
          meta: event.meta,
        };
      }
      return {
        ...current,
        state: NOTIFICATION_LOG_STATE__ABORTED,
        error: event.payload,
        meta: event.meta,
      };
    }
    case NOTIFICATION_EVENT_TYPE__ATTEMPT:
      return {
        ...current,
        // NOTE: Defensive only. The collection schema allows payload to be
        //       null and every other branch here already guards against it,
        //       but no producer currently emits an ATTEMPT without one, and a
        //       scan of production found no such document. Falling back to
        //       what we already have keeps the address and the sms/email icon
        //       when a payload is missing, rather than blanking the row.
        deliveryType:
          (event.payload && event.payload.deliveryType) || current.deliveryType,
        deliveryAddress:
          (event.payload && event.payload.deliveryAddress) ||
          current.deliveryAddress,
        state: NOTIFICATION_LOG_STATE__SENDING,
        attemptedAt: event.meta && event.meta.timestamp,
        sentAt: null,
        error: null,
        meta: event.meta,
      };
    case NOTIFICATION_EVENT_TYPE__ACCEPT: {
      // The relay sends the message_delivery row as the payload, and the row
      // has its own accepted_at. Prefer it: the event's timestamp is only when
      // the provider wrote the event, since an ACCEPTED row has no received_at
      // for the relay to pass on.
      const acceptedAt = event.payload && new Date(event.payload.acceptedAt);
      return {
        ...current,
        state: NOTIFICATION_LOG_STATE__ACCEPTED,
        acceptedAt:
          acceptedAt && !Number.isNaN(acceptedAt.getTime())
            ? acceptedAt
            : event.meta && event.meta.timestamp,
        meta: event.meta,
      };
    }
    case NOTIFICATION_EVENT_TYPE__SEND:
      return {
        ...current,
        state: NOTIFICATION_LOG_STATE__SENT,
        sentAt: event.meta && event.meta.timestamp,
        meta: event.meta,
      };
    case NOTIFICATION_EVENT_TYPE__DELIVER:
      return {
        ...current,
        state: NOTIFICATION_LOG_STATE__DELIVERED,
        deliveredAt: event.meta && event.meta.timestamp,
        meta: event.meta,
        priceInUSD: event.payload && event.payload.priceInUSD,
        dwellTimeMsUntilDeviceAck:
          event.payload && event.payload.dwellTimeMsUntilDeviceAck,
      };
    case NOTIFICATION_EVENT_TYPE__DELIVERY_FAILURE:
      return {
        ...current,
        state: NOTIFICATION_LOG_STATE__FAILED,
        error: event.payload || {
          message: "We couldn't deliver the message",
        },
        meta: event.meta,
      };
    case NOTIFICATION_EVENT_TYPE__RECEIVE:
      return {
        ...current,
        state: NOTIFICATION_LOG_STATE__RECEIVED,
        receivedAt: event.meta && event.meta.timestamp,
        meta: event.meta,
      };
    case NOTIFICATION_EVENT_TYPE__ENQUEUE:
      return {
        ...current,
        state: NOTIFICATION_LOG_STATE__ENQUEUED,
        enqueuedAt: event.meta && event.meta.timestamp,
        meta: event.meta,
      };
    case NOTIFICATION_EVENT_TYPE__OPEN:
      return {
        ...current,
        state: NOTIFICATION_LOG_STATE__OPENED,
        openedAt: event.meta && event.meta.timestamp,
        meta: event.meta,
      };
    case NOTIFICATION_EVENT_TYPE__CLICK:
      return {
        ...current,
        state: NOTIFICATION_LOG_STATE__CLICKED,
        clickedAt: event.meta && event.meta.timestamp,
        meta: event.meta,
      };
    default:
      return current;
  }
};

// How many parts the message was billed as, read off the array rather than
// folded with everything else.
//
// How far a message got depends on the order the events are read in. Which
// send a part count belongs to does not. Settling both in the same fold tied
// the second to the first, so [.., FAILED{5}, RECEIVE] kept the count while
// [.., RECEIVE, FAILED{5}] -- the same failure and the same patient click,
// stored the other way round -- lost it.
//
// A delivery confirmation wins over a failure report: when a log holds both
// they describe two different sends and only one of them arrived. Between two
// events of the same kind the later one wins, and there nothing but position
// separates them; without per-event provider timestamps that is as good as it
// gets, and both describe the same send anyway.
//
// priceInUSD and dwellTimeMsUntilDeviceAck stay in the fold on purpose. Only
// DELIVER ever reports them, so there is no second event type to disagree
// with and no ordering question to answer.
//
// Note the field is absent from production: not one event carries it in QLD,
// PQSS or ANZ. It only ever arrived on the raw AWS SNS delivery payload, and
// every current provider goes through messaging-apigateway, which reports
// `messagesSize` under a different name. Kept correct anyway, because a
// provider that does send it would otherwise be billed from whichever webhook
// happened to be written first.
const resolveNumberOfMessageParts = (events) => {
  let fromDelivery;
  let fromFailure;
  (events || []).forEach((event) => {
    if (!event || !event.payload) {
      return;
    }
    const count = event.payload.numberOfMessageParts;
    if (count === undefined || count === null) {
      return;
    }
    if (event.type === NOTIFICATION_EVENT_TYPE__DELIVER) {
      fromDelivery = count;
    } else if (event.type === NOTIFICATION_EVENT_TYPE__DELIVERY_FAILURE) {
      fromFailure = count;
    }
  });
  return fromDelivery === undefined ? fromFailure : fromDelivery;
};

const progress = (state) =>
  NOTIFICATION_LOG_STATE_PROGRESS[state] === undefined
    ? -1
    : NOTIFICATION_LOG_STATE_PROGRESS[state];

// What the displayed status is made of. An event that arrives too late to
// change the status must not touch these, or it would undo the progress that
// has already been established.
const STATUS_FIELDS = ['state', 'meta', 'error'];

// An error describes why a delivery ended badly, so it only belongs to a state
// that ended badly. Without this a failure from an abandoned first send would
// still be attached after a later attempt succeeded.
const withErrorOnlyWhenFailed = (result) => {
  if (
    result.error === undefined ||
    result.state === NOTIFICATION_LOG_STATE__FAILED ||
    result.state === NOTIFICATION_LOG_STATE__ABORTED
  ) {
    return result;
  }
  return { ...result, error: null };
};

const reducer = (current = {}, event, wasAttempted) => {
  const next = applyEvent(current, event, wasAttempted);
  // Strictly greater, not greater or equal. A repeat of the state we are
  // already in tells us nothing new about the status, and taking its whole
  // result would let a second delivery receipt that arrived without a payload
  // blank the part count and price the first one established.
  if (progress(next.state) > progress(current.state)) {
    return withErrorOnlyWhenFailed(next);
  }
  // NOTE: Array order is not time order. ATTEMPT and ENQUEUE used to carry no
  //       timestamp of their own, so NotificationLogs.emit stamped them when
  //       the queue was drained -- long after the provider had confirmed
  //       delivery. That is fixed at the producers now, but every log written
  //       before the fix still holds those events, and provider stamps are not
  //       monotonic between the two pipelines anyway. Folding an event in
  //       stored order would walk the status back to "sending" for a message
  //       the patient has already answered.
  //
  //       Keep what this event genuinely reports -- its own timestamp, the
  //       message part count, the price -- but ignore the fields it merely
  //       left blank. A branch here writes `undefined` for anything its
  //       payload did not carry and ATTEMPT deliberately nulls `sentAt`, and
  //       neither is knowledge about the state we are keeping.
  const merged = { ...current };
  Object.keys(next).forEach((key) => {
    if (STATUS_FIELDS.indexOf(key) >= 0) {
      return;
    }
    if (next[key] !== undefined && next[key] !== null) {
      merged[key] = next[key];
    }
  });
  return merged;
};

class NotificationLog extends BaseModel {
  get current() {
    const wasAttempted = (this.events || []).some(
      (event) => event && event.type === NOTIFICATION_EVENT_TYPE__ATTEMPT,
    );
    // lodash passes the index as the third argument, so the fold has to be
    // wrapped rather than handed `reducer` directly.
    const fold = (accumulator, event) =>
      reducer(accumulator, event, wasAttempted);
    const folded = reduce(
      this.events,
      fold,
      fold(
        {
          deliveryType: this.deliveryType,
          deliveryAddress: this.deliveryAddress,
        },
        {
          type: '@INIT',
        },
      ),
    );
    const current = {
      ...folded,
      numberOfMessageParts: resolveNumberOfMessageParts(this.events),
    };
    Object.defineProperty(this, 'current', {
      value: current,
    });
    return current;
  }

  getAddress() {
    return this.current.deliveryAddress;
  }

  getLastDeliveryAttemptTime() {
    return this.current.attemptedAt;
  }

  getTimestamp() {
    return this.current.meta && this.current.meta.timestamp;
  }

  getType() {
    return this.current.deliveryType;
  }

  getIcon() {
    switch (this.current.deliveryType) {
      case NOTIFICATION_DELIVERY_TYPE_PUSH:
      case NOTIFICATION_DELIVERY_TYPE_SMS:
        return 'mobile';
      case NOTIFICATION_DELIVERY_TYPE_EMAIL:
        return 'mail';
      default:
        return null;
    }
  }

  getState() {
    return this.current.state;
  }

  getNumberOfMessageParts() {
    return this.current.numberOfMessageParts;
  }

  getPriceInUSD() {
    return this.current.priceInUSD;
  }

  getDwellTimeMsUntilDeviceAck() {
    return this.current.dwellTimeMsUntilDeviceAck;
  }

  isAborted() {
    return this.getState() === NOTIFICATION_LOG_STATE__ABORTED;
  }

  isSent() {
    return this.getState() === NOTIFICATION_LOG_STATE__SENT;
  }

  isDelivered() {
    return this.getState() === NOTIFICATION_LOG_STATE__DELIVERED;
  }

  isReceived() {
    return this.getState() === NOTIFICATION_LOG_STATE__RECEIVED;
  }
}

NotificationLog.collection = 'NotificationLogs';
NotificationLog.piiFields = [
  'deliveryAddress',
  'events.payload.deliveryAddress',
];

export default NotificationLog;
